/**
 * Personal Data Header
 * Displays compact modules for Calendar, Email, and Tasks in the header
 * Conditionally renders modules based on user's app_permissions
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';

interface Task {
  id: string;                         // 5-char alphanumeric ID
  title: string;
  description?: string;
  status: string;
  priority: string;
  due_date?: string;
  created_by: 'user' | 'ai';
  assigned_to: 'user' | 'ai';
  is_blocked?: boolean;
}

interface Event {
  id: number;
  title: string;
  start_time: string;
  end_time: string;
  location?: string;
  description?: string;
}

interface Email {
  id: number;
  sender: string;
  subject: string;
  body?: string;
  recipient?: string;
  email_type?: string;
  is_read: number;
  is_starred: number;
  received_at: string;
}

interface Knowledge {
  id: number;
  title: string;
  content: string;
  category: string;
  created_at: string;
  updated_at: string;
}

interface PersonalDataSummary {
  tasks: {
    pending: Task[];
    for_review: Task[];
    ready?: Task[];  // Deprecated alias for for_review
    in_progress: Task[];
    blocked: Task[];
    completed_recent: Task[];
  };
  events: {
    upcoming: Event[];
    today: Event[];
    this_week: Event[];
  };
  emails: {
    unread: Email[];
    recent: Email[];
    drafts: Email[];
    sent: Email[];
  };
  knowledge: Knowledge[];
}

// Add props interface
interface PersonalDataHeaderProps {
  onRefreshReady?: (refreshFn: () => void) => void;
}

const PersonalDataHeader: React.FC<PersonalDataHeaderProps> = ({ onRefreshReady }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('common');
  const { user } = useAuth();

  // Map i18n language to locale for date formatting
  const dateLocale = i18n.language === 'it' ? 'it-IT' : 'en-US';
  const [data, setData] = useState<PersonalDataSummary | null>(null);
  const [activeModule, setActiveModule] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [emailUnreadCount, setEmailUnreadCount] = useState<number>(0);

  // Get permissions with defaults (all enabled if not specified)
  const permissions = user?.app_permissions || {
    calendar: true,
    email: true,
    knowledge_base: true,
    cogito: true,
    tasks: true,
    disk: true,
    people: true,
    chat: true,
    contractors: true
  };


  // Fetch personal data summary
  useEffect(() => {
    fetchPersonalData();
    // Expose refresh function to parent
    if (onRefreshReady) {
      onRefreshReady(fetchPersonalData);
    }
    // Refresh every 30 seconds as backup
    const interval = setInterval(fetchPersonalData, 30000);
    return () => clearInterval(interval);
  }, [onRefreshReady]);

  // Fetch email unread count from aegis5
  useEffect(() => {
    if (permissions.email === false) {
      return;
    }
    fetchEmailUnreadCount();
    // Refresh email count every 30 seconds
    const interval = setInterval(fetchEmailUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [permissions.email]);

  const fetchPersonalData = async () => {
    try {
      const response = await apiClient.get('/personal-data/summary');
      setData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch personal data:', error);
      setLoading(false);
    }
  };

  const fetchEmailUnreadCount = async () => {
    try {
      const response = await apiClient.get('/email/unread-count');
      setEmailUnreadCount(response.data.inbox_unread || response.data.unread_count || 0);
    } catch (error) {
      // Silently fail - user might not have email accounts set up
      console.debug('Email unread count not available:', error);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const today = now.toDateString();
    const dateStr = date.toDateString();

    if (dateStr === today) {
      return date.toLocaleTimeString(dateLocale, { hour: 'numeric', minute: '2-digit' });
    }
    return date.toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' });
  };

  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleTimeString(dateLocale, { hour: 'numeric', minute: '2-digit' });
  };

  const toggleModule = (module: string) => {
    setActiveModule(activeModule === module ? null : module);
  };

  if (loading) {
    return (
      <div className="personal-data-header">
        <div className="loading-indicator">{t('buttons.loading')}</div>
      </div>
    );
  }

  const pendingTasks = data?.tasks?.pending || [];
  const forReviewTasks = data?.tasks?.for_review || data?.tasks?.ready || [];
  const inProgressTasks = data?.tasks?.in_progress || [];
  const blockedTasks = data?.tasks?.blocked || [];
  const todayEvents = data?.events?.today || [];
  const upcomingEvents = data?.events?.upcoming || [];
  const thisWeekEvents = data?.events?.this_week || [];
  const knowledgeEntries = data?.knowledge || [];

  // Generate calendar week view
  const generateWeekView = () => {
    const now = new Date();
    const currentDay = now.getDay(); // 0 = Sunday, 6 = Saturday
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - currentDay); // Start from Sunday

    const week = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + i);

      // Get events for this day (use this_week which includes past days)
      const dayStr = day.toDateString();
      const dayEvents = thisWeekEvents.filter(event => {
        const eventDate = new Date(event.start_time);
        return eventDate.toDateString() === dayStr;
      });

      week.push({
        date: day,
        dayName: day.toLocaleDateString(dateLocale, { weekday: 'short' }),
        dayNumber: day.getDate(),
        isToday: day.toDateString() === now.toDateString(),
        events: dayEvents
      });
    }
    return week;
  };

  const weekView = generateWeekView();

  const totalTasks = pendingTasks.length + forReviewTasks.length + inProgressTasks.length + blockedTasks.length;
  const totalEvents = todayEvents.length + upcomingEvents.length;
  const totalUnread = emailUnreadCount;  // From aegis5 email API
  const totalKnowledge = knowledgeEntries.length;

  return (
    <div className="personal-data-header">
      {/* People & Companies Module (CRM) - FIRST for easy access */}
      {permissions.people !== false && (
      <div className="header-module">
        <button
          className="module-button"
          onClick={() => navigate('/people')}
          title={t('modules.openPeople')}
        >
          <svg className="module-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <span className="module-label">{t('modules.people')}</span>
        </button>
      </div>
      )}

      {/* Calendar Module */}
      {permissions.calendar !== false && (
      <div className="header-module">
        <button
          className={`module-button ${activeModule === 'calendar' ? 'active' : ''}`}
          onClick={() => {
            // On mobile, navigate directly. On desktop, toggle dropdown
            if (window.innerWidth < 768) {
              navigate('/calendar');
            } else {
              toggleModule('calendar');
            }
          }}
          onDoubleClick={() => navigate('/calendar')}
          title={t('modules.clickToViewSummary')}
        >
          <svg className="module-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className="module-label">{t('modules.calendar')}</span>
          {totalEvents > 0 && (
            <span className="module-badge">{totalEvents}</span>
          )}
        </button>

        {activeModule === 'calendar' && (
          <div className="module-dropdown">
            <div className="dropdown-header">
              <div className="flex justify-between items-center">
                <h3>{t('personalData.calendarDash')} {new Date().toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</h3>
                <button
                  onClick={() => navigate('/calendar')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  {t('buttons.viewAll')} →
                </button>
              </div>
            </div>
            <div className="dropdown-content">
              {/* Week View Calendar Grid */}
              <div className="calendar-grid">
                {weekView.map((day, index) => (
                  <div
                    key={index}
                    className={`calendar-day ${day.isToday ? 'today' : ''}`}
                  >
                    <div className="calendar-day-header">
                      <div className="calendar-day-name">{day.dayName}</div>
                      <div className={`calendar-day-number ${day.isToday ? 'today-number' : ''}`}>
                        {day.dayNumber}
                      </div>
                    </div>
                    <div className="calendar-day-events">
                      {day.events.length > 0 ? (
                        day.events.slice(0, 4).map((event) => (
                          <div key={event.id} className="calendar-event">
                            <div className="calendar-event-time">{formatTime(event.start_time)}</div>
                            <div className="calendar-event-title">{event.title}</div>
                            {event.location && (
                              <div className="calendar-event-location">📍 {event.location}</div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="calendar-no-events">-</div>
                      )}
                      {day.events.length > 4 && (
                        <div className="calendar-more-events">
                          {t('personalData.moreEvents', { count: day.events.length - 4 })}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {totalEvents === 0 && (
                <div className="empty-state mt-6">{t('personalData.noEventsThisWeek')}</div>
              )}
            </div>
          </div>
        )}
      </div>
      )}

      {/* Email Module - Single unified button */}
      {permissions.email !== false && (
      <div className="header-module">
        <button
          className={`module-button ${activeModule === 'email' ? 'active' : ''}`}
          onClick={() => navigate('/email')}
          title={t('modules.openEmail')}
        >
          <svg className="module-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <span className="module-label">{t('modules.email')}</span>
          {totalUnread > 0 && (
            <span className="module-badge unread">{totalUnread}</span>
          )}
        </button>
      </div>
      )}

      {/* Tasks Module */}
      {permissions.tasks !== false && (
      <div className="header-module">
        <button
          className={`module-button ${activeModule === 'tasks' ? 'active' : ''}`}
          onClick={() => {
            if (window.innerWidth < 768) {
              navigate('/tasks');
            } else {
              toggleModule('tasks');
            }
          }}
          onDoubleClick={() => navigate('/tasks')}
          title={t('modules.clickToViewSummary')}
        >
          <svg className="module-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
          <span className="module-label">{t('modules.tasks')}</span>
          {totalTasks > 0 && (
            <span className="module-badge">{totalTasks}</span>
          )}
        </button>

        {activeModule === 'tasks' && (
          <div className="module-dropdown">
            <div className="dropdown-header">
              <div className="flex justify-between items-center">
                <h3>{t('modules.tasks')}</h3>
                <button
                  onClick={() => navigate('/tasks')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  {t('buttons.viewAll')} →
                </button>
              </div>
            </div>
            <div className="dropdown-content">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 w-full">
                {blockedTasks.length > 0 && (
                  <div className="task-section">
                    <h4 className="text-red-600">🚫 {t('personalData.taskBlocked')}</h4>
                    {blockedTasks.slice(0, 5).map((task) => (
                      <div
                        key={task.id}
                        className="task-item blocked border-l-4 border-red-500 cursor-pointer hover:bg-red-50 transition-colors"
                        onClick={() => navigate(`/tasks/${task.id}`)}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="task-title flex items-center gap-2">
                            <span className="text-xs" title={task.assigned_to === 'ai' ? t('personalData.aiTask') : t('personalData.yourTask')}>
                              {task.assigned_to === 'ai' ? '🤖' : '👤'}
                            </span>
                            {task.title}
                          </div>
                          <div className={`task-priority priority-${task.priority}`}>
                            {t(`personalData.priority.${task.priority}`)}
                          </div>
                        </div>
                        <div className="text-xs text-gray-400 font-mono">{task.id}</div>
                      </div>
                    ))}
                  </div>
                )}
                {inProgressTasks.length > 0 && (
                  <div className="task-section">
                    <h4 className="text-yellow-600">⏳ {t('personalData.taskInProgress')}</h4>
                    {inProgressTasks.slice(0, 10).map((task) => (
                      <div
                        key={task.id}
                        className="task-item in-progress border-l-4 border-yellow-500 cursor-pointer hover:bg-yellow-50 transition-colors"
                        onClick={() => navigate(`/tasks/${task.id}`)}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="task-title flex items-center gap-2">
                            <span className="text-xs" title={task.assigned_to === 'ai' ? t('personalData.aiTask') : t('personalData.yourTask')}>
                              {task.assigned_to === 'ai' ? '🤖' : '👤'}
                            </span>
                            {task.title}
                          </div>
                          <div className={`task-priority priority-${task.priority}`}>
                            {t(`personalData.priority.${task.priority}`)}
                          </div>
                        </div>
                        {task.due_date && (
                          <div className="task-due">{t('personalData.dueLabel')} {formatDate(task.due_date)}</div>
                        )}
                        <div className="text-xs text-gray-400 font-mono">{task.id}</div>
                      </div>
                    ))}
                  </div>
                )}
                {forReviewTasks.length > 0 && (
                  <div className="task-section">
                    <h4 className="text-blue-600">✅ {t('personalData.taskForReview')}</h4>
                    {forReviewTasks.slice(0, 10).map((task) => (
                      <div
                        key={task.id}
                        className="task-item ready border-l-4 border-blue-500 cursor-pointer hover:bg-blue-50 transition-colors"
                        onClick={() => navigate(`/tasks/${task.id}`)}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="task-title flex items-center gap-2">
                            <span className="text-xs" title={task.assigned_to === 'ai' ? t('personalData.aiTask') : t('personalData.yourTask')}>
                              {task.assigned_to === 'ai' ? '🤖' : '👤'}
                            </span>
                            {task.title}
                          </div>
                          <div className={`task-priority priority-${task.priority}`}>
                            {t(`personalData.priority.${task.priority}`)}
                          </div>
                        </div>
                        {task.due_date && (
                          <div className="task-due">{t('personalData.dueLabel')} {formatDate(task.due_date)}</div>
                        )}
                        <div className="text-xs text-gray-400 font-mono">{task.id}</div>
                      </div>
                    ))}
                  </div>
                )}
                {pendingTasks.length > 0 && (
                  <div className="task-section">
                    <h4 className="text-gray-600">⏸️ {t('personalData.taskPending')}</h4>
                    {pendingTasks.slice(0, 10).map((task) => (
                      <div
                        key={task.id}
                        className="task-item pending border-l-4 border-gray-400 cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => navigate(`/tasks/${task.id}`)}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="task-title flex items-center gap-2">
                            <span className="text-xs" title={task.assigned_to === 'ai' ? t('personalData.aiTask') : t('personalData.yourTask')}>
                              {task.assigned_to === 'ai' ? '🤖' : '👤'}
                            </span>
                            {task.title}
                          </div>
                          <div className={`task-priority priority-${task.priority}`}>
                            {t(`personalData.priority.${task.priority}`)}
                          </div>
                        </div>
                        {task.due_date && (
                          <div className="task-due">{t('personalData.dueLabel')} {formatDate(task.due_date)}</div>
                        )}
                        <div className="text-xs text-gray-400 font-mono">{task.id}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {totalTasks === 0 && (
                <div className="empty-state">{t('personalData.noTasks')}</div>
              )}
            </div>
          </div>
        )}
      </div>
      )}

      {/* Knowledge Base Module */}
      {permissions.knowledge_base !== false && (
      <div className="header-module">
        <button
          className={`module-button ${activeModule === 'knowledge' ? 'active' : ''}`}
          onClick={() => {
            if (window.innerWidth < 768) {
              navigate('/knowledge');
            } else {
              toggleModule('knowledge');
            }
          }}
          onDoubleClick={() => navigate('/knowledge')}
          title={t('modules.clickToViewSummary')}
        >
          <svg className="module-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          <span className="module-label">{t('modules.knowledge')}</span>
          {totalKnowledge > 0 && (
            <span className="module-badge">{totalKnowledge}</span>
          )}
        </button>

        {activeModule === 'knowledge' && (
          <div className="module-dropdown">
            <div className="dropdown-header">
              <div className="flex justify-between items-center">
                <h3>{t('modules.knowledge')}</h3>
                <button
                  onClick={() => navigate('/knowledge')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  {t('buttons.viewAll')} →
                </button>
              </div>
            </div>
            <div className="dropdown-content">
              {knowledgeEntries.length > 0 ? (
                <div className="space-y-3">
                  {knowledgeEntries.map((entry) => (
                    <div key={entry.id} className="knowledge-item">
                      <div className="knowledge-header">
                        <div className="knowledge-title">{entry.title}</div>
                        <span className="knowledge-category">{entry.category}</span>
                      </div>
                      <div className="knowledge-content">{entry.content}</div>
                      <div className="text-xs text-gray-500 mt-2">{t('personalData.idLabel')} {entry.id}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">{t('knowledge.facts.noEntries')}</div>
              )}
            </div>
          </div>
        )}
      </div>
      )}

      {/* Disk Module */}
      {permissions.disk !== false && (
      <div className="header-module">
        <button
          className="module-button"
          onClick={() => navigate('/disk')}
          title={t('modules.openDisk')}
        >
          <svg className="module-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          <span className="module-label">{t('modules.disk')}</span>
        </button>
      </div>
      )}

      {/* Chat Module */}
      {permissions.chat !== false && (
      <div className="header-module">
        <button
          className="module-button"
          onClick={() => navigate('/chat')}
          title={t('modules.openChat')}
        >
          <svg className="module-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <span className="module-label">{t('modules.chat')}</span>
        </button>
      </div>
      )}

      {/* Contractors Module */}
      {permissions.contractors !== false && (
      <div className="header-module">
        <button
          className="module-button"
          onClick={() => navigate('/contractors')}
          title={t('modules.openContractors')}
        >
          <svg className="module-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <span className="module-label">{t('modules.contractors')}</span>
        </button>
      </div>
      )}

    </div>
  );
};

export default PersonalDataHeader;
